import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type PropsWithChildren,
} from 'react';
import { rpcClient } from 'src/api/rpc/client';
import type {
  LiveTurnState,
  ModuleKey,
  PageKey,
  MessageAttachment,
  GetChatThreadOutput,
  QueueMode,
  ChatAgentChatInput,
  MessageChannel,
} from '@erp/rpc';
import type {
  FrontendToolDefinition,
  FrontendToolResult,
} from '@/hooks/useFrontendTool';
import { sharesThread } from './sharesThread';
import { currentChatWindow } from './chatWindowSize';
import {
  claimPendingAwaitableCalls,
  markClaimed,
  runAwaitableTool,
  toolsByName,
  type RunAwaitableToolDeps,
} from './frontendToolDispatch';
import { restoreMessages } from './restoreMessages';
import {
  followLiveTurn,
  liveToolCalls,
  rememberThread,
  rememberedThread,
  persistedClaims,
  ADMISSION_WAIT_MS,
  STOP_WAIT_MS,
  stopTurnOnServer,
  waitAtMost,
} from './liveTurn';
import {
  queueAllowed,
  queuedFromTurn,
  SubmissionRefusedError,
  submitToBusyThread,
  withdrawOnServer,
  type QueuedMessage,
} from './chatInbox';

export interface MessageToolResult {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  result: unknown;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolResults?: MessageToolResult[];
  attachments?: MessageAttachment[];
  /**
   * Stable DB id (`ai_thread_messages.id`) for assistant turns once the
   * backend has persisted them — used as `subjectId` for per-message user
   * feedback. Absent on user turns and on assistant turns that failed to
   * persist (e.g., DB write error after streaming).
   */
  dbMessageId?: string;
  /** Langfuse traceId for the turn that produced this assistant message. */
  traceId?: string | null;
  /**
   * Langfuse observation (span) id for the AI SDK's `ai.streamText` span
   * within the trace. When present, user-feedback scores attach to this
   * specific observation; otherwise they fall back to trace-level.
   */
  observationId?: string | null;
  hasScreenshot?: boolean;
  /** Base64-encoded screenshot data URL for display in the chat bubble. */
  screenshotDataUrl?: string;
  /**
   * Short label rendered above the bubble to set it apart from the main
   * conversation — e.g. "Setup procedure" for lines from a setup SOP replayed
   * inline before an operator interview's live teaching turn begins.
   */
  badge?: string;
  /** Spoken on a voice call, or the reply to something spoken. */
  channel?: MessageChannel;
}

export type ImageAttachmentValue = {
  imageData: string;
  mimeType: 'image/jpeg' | 'image/png';
};

export interface ToolCall {
  toolCallId: string;
  toolName: string;
  userFacingName?: string;
  isWrite?: boolean;
  createdAt?: number;
  /** The reply's text written before this call. */
  textBefore?: string;
  args: Record<string, unknown>;
  result?: unknown;
  status: 'pending' | 'complete' | 'error';
}

/**
 * Generic "agent yields to the user" primitive. When a tool call produces a
 * result that requires a user decision (accept / discard / …) before the
 * module state should change, the owning module parks an `AgentYield` on
 * agent state and the chat card surfaces it. The context itself knows
 * nothing about the specific payload shape — `payload` is `unknown` and
 * consumers cast at their own boundary. Today's concrete use case is the
 * workflow-proposal Apply Changes card; future yields might be clarifying
 * questions, approval requests, or anything else that pauses the agent for
 * user input.
 */
export interface AgentYield {
  /** Stable id for this specific yield (usually derived from toolCallId). */
  yieldId: string;
  /** The tool call whose result produced this yield. */
  toolCallId: string;
  /** Name of the tool that produced the yield (for consumer dispatch). */
  toolName: string;
  /** Optional human-readable label for the chat card. */
  summary?: string;
  /** Module-specific payload. Consumers cast to their type. */
  payload: unknown;
  createdAt: Date;
}

/** Decision string recorded when the user resolves a yield. */
export type YieldDecision = 'accepted' | 'discarded';

/**
 * How a send ended: `rejected` when the server refused to start the turn
 * (the per-user turn cap, AI-527) and nothing was saved, so the composer
 * should offer the text again.
 */
export type SendOutcome = 'sent' | 'rejected';

type ToolCallListener = (
  toolCallId: string,
  toolName: string,
  args: Record<string, unknown>,
  isWrite?: boolean,
) => void;

type ToolResultListener = (
  toolCallId: string,
  toolName: string,
  result: unknown,
) => void;

type DraftSnapshotListener = (toolCallId: string, draft: unknown) => void;

type ToolInputStreamListener = (
  toolCallId: string,
  toolName: string,
  partialInput: string,
) => void;

export type { QueuedMessage } from './chatInbox';

export interface AgentStateContextData {
  messages: Message[];
  /**
   * Messages sent while the turn runs (AI-518), waiting for it to place
   * them: a steer after its current tool round, a follow-up after it ends.
   */
  queuedMessages: readonly QueuedMessage[];
  /** Takes a queued message back before the turn places it. */
  withdrawQueued: (requestId: string) => void;
  /**
   * Whether a message sent now from `moduleKey` queues behind the streaming
   * reply instead of starting a turn (it must continue the same thread).
   */
  canQueueFor: (moduleKey: ModuleKey) => boolean;
  streamingText: string;
  isLoading: boolean;
  /**
   * The submission the live reply answers. It changes when a queued message is
   * placed, so consecutive replies of one busy stream are told apart.
   */
  activeRequestId: string | null;
  isCapturingScreen: boolean;
  error: string | null;
  activeToolCalls: ToolCall[];
  currentThreadId: string | null;
  currentModuleKey: ModuleKey | null;
  /**
   * Yields currently awaiting a user decision. Today the UX rule is
   * "second proposal replaces the first", so this list holds at most one
   * entry — modelled as a list so we can lift that restriction later
   * without widening types across consumers.
   */
  activeYields: readonly AgentYield[];
  /**
   * Replace the set of active yields. Modules call this from a
   * tool-result listener when a result represents a pending decision.
   * Today callers pass a single-element array (or empty to clear);
   * future work can pass multiple.
   */
  setActiveYields: (next: readonly AgentYield[]) => void;
  /**
   * Terminal decision for every yield the user has acted on, keyed by
   * toolCallId. Lives in shared state so the chat card (rendered from
   * ChatAgentRenderer) and the producing module (e.g., WorkflowEditor)
   * see the same value within a single React commit, regardless of child
   * render order.
   */
  resolvedYields: ReadonlyMap<string, YieldDecision>;
  sendMessage: (
    content: string,
    moduleKey: ModuleKey,
    currentPage: string,
    moduleContext?: Record<string, unknown>,
    pageKey?: PageKey,
    frontendTools?: FrontendToolDefinition[],
    imageAttachment?: ImageAttachmentValue | Promise<ImageAttachmentValue>,
    attachments?: MessageAttachment[],
    /**
     * While a turn runs on the same thread the message is queued with this
     * mode (default `followUp`) instead of starting a turn.
     */
    queueMode?: QueueMode,
    /** `voice` when the message was spoken on a call. */
    channel?: MessageChannel,
  ) => Promise<SendOutcome>;
  /**
   * Add a spoken turn the assistant answered by voice alone to the thread:
   * shown now, saved to the server (creating the thread if needed).
   */
  recordVoiceTurn: (
    role: Message['role'],
    content: string,
    moduleKey: ModuleKey,
  ) => void;
  reset: () => void;
  cancel: () => void;
  /**
   * Rehydrate a persisted conversation so the user can keep talking in it.
   * Replaces the in-memory transcript with the thread's stored messages and
   * adopts its `threadId`, so the next `sendMessage` appends to the same
   * `ai_threads` row instead of starting a new one. System messages are
   * dropped — the chat UI only renders user/assistant turns.
   */
  loadThread: (thread: GetChatThreadOutput, moduleKey: ModuleKey) => void;
  /**
   * Record the outcome of a specific yield for telemetry and remove it
   * from the active list. Callers apply their module-specific side
   * effects (commit workflow, etc.) themselves before/after calling
   * this — the context only owns yield state and fires the telemetry
   * RPC.
   */
  resolveYield: (toolCallId: string, decision: YieldDecision) => void;
  /**
   * Clear the terminal decision for a specific yield, e.g. when the user
   * reverts an applied yield and the chat card should return to an
   * actionable state.
   */
  clearYieldResolution: (toolCallId: string) => void;
  /**
   * Subscribe to completed tool-result events as they stream in. Modules
   * use this to react to tool-results (e.g., to populate an `AgentYield`)
   * without the context itself needing to know tool names. Returns an
   * unregister function.
   */
  registerToolResultListener: (listener: ToolResultListener) => () => void;
  registerToolCallListener: (listener: ToolCallListener) => () => void;
  registerDraftSnapshotListener: (
    listener: DraftSnapshotListener,
  ) => () => void;
  /**
   * Subscribe to streaming partial tool input as the model writes a tool
   * call's JSON arguments, before the call completes. Modules use this to
   * progressively render an incomplete spec (e.g. an email-template
   * proposal streaming into the editor/preview). Returns an unregister
   * function.
   */
  registerToolInputStreamListener: (
    listener: ToolInputStreamListener,
  ) => () => void;
  /**
   * Tells the context which frontend tools the page currently offers (and the
   * page's module). A tab that reloaded mid-turn reattaches to the turn with
   * no `sendMessage` call to carry them, so it runs the pending awaitable
   * calls through these instead — now, and again whenever the set changes,
   * since a page's tools register only once its data has loaded.
   */
  setLiveFrontendTools: (
    tools: readonly FrontendToolDefinition[],
    moduleKey: ModuleKey | null,
    /** The page's entity, e.g. `workbook:<id>`: pending calls run only on the page their turn started on. */
    scope?: string | null,
  ) => void;
}

const AgentStateContext = createContext<AgentStateContextData | undefined>(
  undefined,
);

/** No Stop in flight: a send has nothing to wait for. */
const NO_STOP: Promise<void> = Promise.resolve();

let messageIdCounter = 0;

/**
 * Safety guard only: scopes are dropped when their request settles or is
 * withdrawn. Well above one active turn plus the server's queue (10).
 */
const MAX_REMEMBERED_SCOPES = 32;

function generateMessageId(): string {
  return `msg-${Date.now()}-${++messageIdCounter}`;
}

/** The message as the chat procedure takes it: text, an optional screenshot, files. */
function toApiMessage(
  content: string,
  image: ImageAttachmentValue | undefined,
  attachments: MessageAttachment[] | undefined,
  channel?: MessageChannel,
) {
  return {
    ...(channel && { channel }),
    content: image
      ? [
          { type: 'text' as const, text: content },
          {
            type: 'image' as const,
            imageData: image.imageData,
            mimeType: image.mimeType,
          },
        ]
      : content,
    ...(attachments?.length && { attachments }),
  };
}

/** Schemas of the frontend tools offered this turn (`enabled: false` ones are not). */
function activeToolSchemas(tools: FrontendToolDefinition[] | undefined) {
  const schemas = (tools ?? []).flatMap((t) =>
    t.enabled === false ? [] : [t.schema],
  );
  return schemas.length ? schemas : undefined;
}

/** Why the provider is reattaching to a turn: see `attachToRunningTurn`. */
type AttachMode = 'refresh' | 'open' | 'drop' | 'placed';

// fallow-ignore-next-line complexity -- pre-existing 700-line provider; AI-520 adds the reattach callbacks to it.
export function AgentStateProvider({ children }: PropsWithChildren) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [queuedMessages, setQueuedMessages] = useState<
    readonly QueuedMessage[]
  >([]);
  const [streamingText, setStreamingText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [isCapturingScreen, setIsCapturingScreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeToolCalls, setActiveToolCalls] = useState<ToolCall[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [currentModuleKey, setCurrentModuleKey] = useState<ModuleKey | null>(
    null,
  );
  const [activeYields, setActiveYieldsState] = useState<readonly AgentYield[]>(
    [],
  );
  const [resolvedYields, setResolvedYields] = useState<
    Map<string, YieldDecision>
  >(() => new Map());

  const setActiveYields = useCallback((next: readonly AgentYield[]) => {
    setActiveYieldsState(next);
  }, []);

  const toolCallListenersRef = useRef<Set<ToolCallListener>>(new Set());
  const registerToolCallListener = useCallback((listener: ToolCallListener) => {
    toolCallListenersRef.current.add(listener);
    return () => {
      toolCallListenersRef.current.delete(listener);
    };
  }, []);

  const toolResultListenersRef = useRef<Set<ToolResultListener>>(new Set());
  const registerToolResultListener = useCallback(
    (listener: ToolResultListener) => {
      toolResultListenersRef.current.add(listener);
      return () => {
        toolResultListenersRef.current.delete(listener);
      };
    },
    [],
  );

  const draftSnapshotListenersRef = useRef<Set<DraftSnapshotListener>>(
    new Set(),
  );
  const registerDraftSnapshotListener = useCallback(
    (listener: DraftSnapshotListener) => {
      draftSnapshotListenersRef.current.add(listener);
      return () => {
        draftSnapshotListenersRef.current.delete(listener);
      };
    },
    [],
  );

  const toolInputStreamListenersRef = useRef<Set<ToolInputStreamListener>>(
    new Set(),
  );
  const registerToolInputStreamListener = useCallback(
    (listener: ToolInputStreamListener) => {
      toolInputStreamListenersRef.current.add(listener);
      return () => {
        toolInputStreamListenersRef.current.delete(listener);
      };
    },
    [],
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  const streamSessionIdRef = useRef(0);
  // Bumped by every user-initiated conversation change, so a refresh restore
  // still waiting on its fetch knows it no longer owns the screen.
  const restoreGenerationRef = useRef(0);
  const threadIdRef = useRef(currentThreadId);
  threadIdRef.current = currentThreadId;
  const moduleKeyRef = useRef(currentModuleKey);
  moduleKeyRef.current = currentModuleKey;
  /** The in-flight spoken-turn save chain; a send waits for its thread. */
  const voiceWriteRef = useRef<Promise<void> | undefined>(undefined);
  /**
   * Settles once a send that is creating a new thread has learned its id, so
   * a spoken turn saved meanwhile joins that thread instead of making another.
   */
  const threadCreatingRef = useRef<Promise<void> | undefined>(undefined);
  /**
   * The conversation on screen, replaced whenever another takes its place
   * (reset, another thread, another module). A spoken turn is saved to the
   * conversation it was spoken in, even if that is no longer the one shown.
   */
  const conversationRef = useRef<{ threadId: string | null }>({
    threadId: null,
  });
  const leaveConversation = useCallback(() => {
    conversationRef.current.threadId = threadIdRef.current;
    conversationRef.current = { threadId: null };
  }, []);
  // Tracks the pageKey of the most recently sent message so resolveYield can
  // stamp it on the yield-decision telemetry without callers re-passing it.
  const pageKeyRef = useRef<PageKey | undefined>(undefined);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Turns run detached on the server (AI-520): aborting the stream only
  // stops watching, so an explicit Stop also tells the server.
  const isLoadingRef = useRef(isLoading);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  // The server run behind the current stream, from `thread-info` or a
  // snapshot; Stop names it so a late Stop can't end a newer turn.
  const runIdRef = useRef<string | null>(null);
  // Stop pressed before `thread-info`: the stream stays open until the run
  // is known, then the server is told and the stream closed.
  const pendingStopSessionRef = useRef<number | null>(null);
  const attachRef = useRef<
    (threadId: string, moduleKey: ModuleKey, mode: AttachMode) => Promise<void>
  >(async () => {});
  // The last Stop sent; a send waits for it so the stopped turn no longer
  // holds a slot under the per-user cap (AI-527).
  const stopInFlightRef = useRef<Promise<void>>(NO_STOP);
  // The last send not yet admitted by the server. A message queued behind it
  // waits, so it can't reach the server first (the send may be waiting on a
  // Stop) and queue onto the stopping run.
  const admissionRef = useRef<Promise<void>>(NO_STOP);
  // Queued messages still waiting on that admission, not yet sent. Taking
  // one back (withdraw, Stop, reset, another thread) drops it from here, so
  // it is never sent.
  const heldQueuedRef = useRef(new Set<string>());
  /** Leaving the thread: its held messages are never sent, nor shown. */
  const dropHeldQueued = useCallback(() => {
    const held = new Set(heldQueuedRef.current);
    if (held.size === 0) return;
    heldQueuedRef.current.clear();
    setQueuedMessages((prev) => prev.filter((q) => !held.has(q.requestId)));
  }, []);
  const stopRunningTurn = useCallback(() => {
    if (isLoadingRef.current && threadIdRef.current) {
      stopInFlightRef.current = stopTurnOnServer(
        threadIdRef.current,
        runIdRef.current ?? undefined,
      );
    }
  }, []);

  /** The latest snapshot of a turn this tab may answer tools for, else null. */
  const liveTurnRef = useRef<LiveTurnState | null>(null);
  /** The stream session that snapshot belongs to; a newer session voids it. */
  const liveTurnSessionRef = useRef(0);

  const cancel = useCallback(() => {
    setIsLoading(false);
    // Stop withdraws whatever was queued behind the turn.
    setQueuedMessages([]);
    heldQueuedRef.current.clear();
    liveTurnRef.current = null;
    if (isLoadingRef.current && !runIdRef.current) {
      pendingStopSessionRef.current = streamSessionIdRef.current;
      return;
    }
    stopRunningTurn();
    abortControllerRef.current?.abort();
  }, [stopRunningTurn]);

  /** A frontend tool handler failed — show the call as failed, not complete. */
  const markToolCallFailed = useCallback(
    (toolCallId: string, result: FrontendToolResult) => {
      setActiveToolCalls((prev) =>
        prev.map((existing) =>
          existing.toolCallId === toolCallId
            ? { ...existing, result, status: 'error' as const }
            : existing,
        ),
      );
    },
    [],
  );

  /** The page entity now registered. */
  const liveScopeRef = useRef<string | null>(null);
  // A refresh keeps the tab's thread so a turn still running can be resumed.
  // Read once before the effect that remembers the thread overwrites it.
  const [threadBeforeRefresh] = useState(rememberedThread);
  /** The page entity each submission this tab made came from, by request id. */
  const submissionScopesRef = useRef<Record<string, string>>(
    threadBeforeRefresh?.scopes ?? {},
  );
  /** Notes where a submission came from, and keeps it across a refresh. */
  const recordSubmissionScope = useCallback(
    (
      requestId: string,
      moduleKey: ModuleKey,
      remember: boolean,
      scope: string | null,
    ) => {
      if (scope !== null) {
        submissionScopesRef.current = {
          ...Object.fromEntries(
            Object.entries(submissionScopesRef.current).slice(
              -(MAX_REMEMBERED_SCOPES - 1),
            ),
          ),
          [requestId]: scope,
        };
      }
      if (remember && threadIdRef.current) {
        rememberThread({
          threadId: threadIdRef.current,
          moduleKey,
          scopes: submissionScopesRef.current,
        });
      }
    },
    [],
  );
  /** A request settled, was withdrawn, or was refused: its scope is not needed. */
  const forgetSubmissionScope = useCallback((requestId: string | undefined) => {
    if (requestId === undefined || !(requestId in submissionScopesRef.current))
      return;
    const rest = Object.fromEntries(
      Object.entries(submissionScopesRef.current).filter(
        ([id]) => id !== requestId,
      ),
    );
    submissionScopesRef.current = rest;
    const remembered = rememberedThread();
    if (remembered && remembered.threadId === threadIdRef.current) {
      rememberThread({ ...remembered, scopes: rest });
    }
  }, []);
  /** Tool calls whose awaitable handler has been started, by either path. */
  const claimedToolCallsRef = useRef<Set<string> | null>(null);
  const getClaimedToolCalls = useCallback(() => {
    const existing = claimedToolCallsRef.current;
    if (existing) return existing;
    const created = new Set(persistedClaims());
    claimedToolCallsRef.current = created;
    return created;
  }, []);
  const frontendToolRunDeps = useMemo<RunAwaitableToolDeps>(
    () => ({
      submit: (input) => rpcClient.ai.chatAgent.submitFrontendToolResult(input),
      onFailed: markToolCallFailed,
    }),
    [markToolCallFailed],
  );

  /**
   * The thread's turn is running: queue the message behind it (AI-518). The
   * stream already following the turn reports when it is placed. If the
   * turn ended before the message arrived, the server started a new turn
   * for it, which this follows from its record.
   */
  const queueMessage = useCallback(
    async (
      threadId: string,
      input: Omit<ChatAgentChatInput, 'threadId' | 'requestId'>,
      text: string,
      mode: QueueMode,
      originScope: string | null,
    ): Promise<SendOutcome> => {
      restoreGenerationRef.current++;
      const requestId = crypto.randomUUID();
      recordSubmissionScope(requestId, input.moduleKey, true, originScope);
      const dequeue = () =>
        setQueuedMessages((prev) =>
          prev.filter((q) => q.requestId !== requestId),
        );
      setQueuedMessages((prev) => [...prev, { requestId, text, mode }]);
      heldQueuedRef.current.add(requestId);
      try {
        await waitAtMost(admissionRef.current, ADMISSION_WAIT_MS);
        // Taken back while held: never sent.
        if (!heldQueuedRef.current.delete(requestId)) return 'sent';
        const admitted = await submitToBusyThread({
          ...input,
          threadId,
          requestId,
          queueMode: mode,
        });
        if (admitted.status === 'queued') return 'sent';
        dequeue();
        if (admitted.status === 'placed') {
          void attachRef.current(threadId, input.moduleKey, 'placed');
        }
        return 'sent';
      } catch (err) {
        dequeue();
        // Never admitted: nothing will run for it.
        forgetSubmissionScope(requestId);
        setError(err instanceof Error ? err.message : 'Failed to send');
        // Refused for the turn cap: the composer offers the text again.
        return err instanceof SubmissionRefusedError &&
          err.code === 'TOO_MANY_TURNS'
          ? 'rejected'
          : 'sent';
      }
    },
    [forgetSubmissionScope, recordSubmissionScope],
  );

  const withdrawQueued = useCallback(
    (requestId: string) => {
      setQueuedMessages((prev) =>
        prev.filter((q) => q.requestId !== requestId),
      );
      forgetSubmissionScope(requestId);
      // Still held here: it never reached the server, so there is nothing
      // to withdraw there.
      if (heldQueuedRef.current.delete(requestId)) return;
      if (threadIdRef.current) withdrawOnServer(threadIdRef.current, requestId);
    },
    [forgetSubmissionScope],
  );

  const sendMessage = useCallback(
    // fallow-ignore-next-line complexity -- pre-existing stream loop; AI-520 only adds a stopTurn call on module change.
    async (
      content: string,
      moduleKey: ModuleKey,
      currentPage: string,
      moduleContext?: Record<string, unknown>,
      pageKey?: PageKey,
      frontendTools?: FrontendToolDefinition[],
      imageAttachment?: ImageAttachmentValue | Promise<ImageAttachmentValue>,
      attachments?: MessageAttachment[],
      queueMode: QueueMode = 'followUp',
      channel?: MessageChannel,
    ) => {
      // The page this was submitted from, read before any await: the user
      // may navigate while voice saves or a screen capture finish.
      const originScope = liveScopeRef.current;
      // A spoken turn may be creating the thread right now; join it.
      const conversation = conversationRef.current;
      await voiceWriteRef.current;
      // Reset or another thread opened meanwhile: this message was written
      // for a conversation that is gone.
      if (conversationRef.current !== conversation) return 'sent';
      const prevModuleKey = moduleKeyRef.current;
      const moduleChanged =
        prevModuleKey !== null && !sharesThread(prevModuleKey, moduleKey);
      // A turn is running on this thread: the message waits for it. The
      // same rule as `canQueueFor`, which the composers read.
      const busyThreadId = queueAllowed({
        isLoading: isLoadingRef.current,
        threadId: threadIdRef.current,
        currentModuleKey: prevModuleKey,
        moduleKey,
      })
        ? threadIdRef.current
        : null;
      if (busyThreadId) {
        const image = await Promise.resolve(imageAttachment).catch(
          () => undefined,
        );
        return queueMessage(
          busyThreadId,
          {
            moduleKey,
            pageKey,
            currentPage,
            message: toApiMessage(content, image, attachments, channel),
            moduleContext,
            chatWindow: currentChatWindow(),
            frontendTools: activeToolSchemas(frontendTools),
          },
          content,
          queueMode,
          originScope,
        );
      }

      abortControllerRef.current?.abort();
      // This send's own: the ref moves on to a newer send or a reset.
      const controller = new AbortController();
      abortControllerRef.current = controller;

      restoreGenerationRef.current++;
      const myStreamSession = ++streamSessionIdRef.current;
      liveTurnRef.current = null;
      let markAdmitted!: () => void;
      admissionRef.current = new Promise<void>((resolve) => {
        markAdmitted = resolve;
      });
      // This turn's tools belong to the page it was submitted from.
      if (moduleChanged) submissionScopesRef.current = {};
      const requestId = crypto.randomUUID();
      recordSubmissionScope(requestId, moduleKey, !moduleChanged, originScope);

      if (moduleChanged) {
        // A new thread: the old one's turn is left behind, so stop it.
        stopRunningTurn();
        dropHeldQueued();
        leaveConversation();
        setMessages([]);
        setCurrentThreadId(null);
        setActiveYieldsState([]);
        setResolvedYields(new Map());
      }
      setCurrentModuleKey(moduleKey);
      pageKeyRef.current = pageKey;

      const userMessage: Message = {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: new Date(),
        hasScreenshot: !!imageAttachment,
        attachments: attachments?.length ? attachments : undefined,
        ...(channel && { channel }),
      };

      setMessages((prev) =>
        moduleChanged ? [userMessage] : [...prev, userMessage],
      );
      setStreamingText('');
      setIsLoading(true);
      setActiveRequestId(requestId);
      setError(null);
      setActiveToolCalls([]);
      runIdRef.current = null;
      pendingStopSessionRef.current = null;

      /** Releases spoken-turn saves waiting on this send's new thread. */
      let threadNamed: (() => void) | undefined;
      // Known once `thread-info` arrives; a dropped stream reattaches to it.
      let streamThreadId: string | null = null;
      // The submission this stream is answering, and whether a reattach
      // took over (it then settles the scope itself).
      let answeringRequestId: string = requestId;
      let handedOff = false;
      let outcome: SendOutcome = 'sent';
      const threadId = moduleChanged
        ? undefined
        : (threadIdRef.current ?? undefined);
      // Before the screenshot await: a spoken turn saved meanwhile must wait
      // for this send's thread rather than create another.
      if (!threadId) {
        threadCreatingRef.current = new Promise<void>((resolve) => {
          threadNamed = resolve;
        });
      }
      let resolvedImage: ImageAttachmentValue | undefined;
      if (imageAttachment) {
        if (imageAttachment instanceof Promise) {
          setIsCapturingScreen(true);
          try {
            resolvedImage = await imageAttachment;
          } catch (err) {
            console.warn(
              '[agent-state] Screen capture failed, sending without image:',
              err,
            );
          } finally {
            setIsCapturingScreen(false);
          }
        } else {
          resolvedImage = imageAttachment;
        }
        if (resolvedImage) {
          const dataUrl = `data:${resolvedImage.mimeType};base64,${resolvedImage.imageData}`;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === userMessage.id
                ? { ...m, screenshotDataUrl: dataUrl }
                : m,
            ),
          );
        }
      }

      // A Stop just sent (Stop then resend, or a module switch) lands first,
      // unless it stalls.
      const stop = stopInFlightRef.current;
      await waitAtMost(stop, STOP_WAIT_MS);
      // Waited out: a stalled Stop doesn't hold up later sends as well.
      if (stopInFlightRef.current === stop) stopInFlightRef.current = NO_STOP;
      if (controller.signal.aborted) {
        // Reset, another thread, or a newer send while waiting: this message
        // is dropped unsent, and the state belongs to whatever replaced it.
        threadNamed?.();
        markAdmitted();
        forgetSubmissionScope(requestId);
        setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
        return outcome;
      }

      try {
        // Only the new message is sent: the server rebuilds earlier turns,
        // tool calls and results included, from the thread's saved rows.
        const apiMessage = toApiMessage(
          userMessage.content,
          resolvedImage,
          userMessage.attachments,
          channel,
        );

        // A tool with `enabled: false` stays mounted but is not offered to the
        // agent this turn, so it must be absent from both the advertised
        // schemas and the dispatch map.
        const activeFrontendTools = (frontendTools ?? []).filter(
          (t) => t.enabled !== false,
        );
        const frontendToolMap = new Map(
          activeFrontendTools.map((t) => [t.name, t]),
        );

        const iterator = await rpcClient.ai.chatAgent.chat(
          {
            moduleKey,
            pageKey,
            currentPage,
            threadId,
            requestId,
            message: apiMessage,
            // TODO(AI-514 follow-up): drop once every backend (Render and
            // Rexel/Azure) runs a release that reads `message`; servers
            // before #8194 still require `messages`. Newer ones ignore it.
            messages: [{ role: 'user' as const, ...apiMessage }],
            moduleContext,
            chatWindow: currentChatWindow(),
            frontendTools: activeFrontendTools.length
              ? activeFrontendTools.map((t) => t.schema)
              : undefined,
          },
          { signal: controller.signal },
        );

        let fullText = '';
        let completedToolResults: MessageToolResult[] = [];
        /** Ends the reply streamed so far as a message of its own. */
        const closeReply = () => {
          // Persist the assistant turn if it produced text OR tool results.
          // A tool-only turn (e.g. the agent renders a chart with no
          // accompanying prose) still needs a Message so its toolResults —
          // which carry the render payload — reach the UI.
          if (fullText || completedToolResults.length > 0) {
            const assistantMessage: Message = {
              id: generateMessageId(),
              role: 'assistant',
              content: fullText,
              timestamp: new Date(),
              toolResults:
                completedToolResults.length > 0
                  ? completedToolResults
                  : undefined,
              ...(channel && { channel }),
            };
            setMessages((prev) => [...prev, assistantMessage]);
          }
          // One stream can carry several replies when queued messages run.
          fullText = '';
          completedToolResults = [];
          setStreamingText('');
        };
        const localToolCalls: Map<
          string,
          { toolName: string; args: Record<string, unknown> }
        > = new Map();

        for await (const event of iterator) {
          // The server answered: anything queued behind this send lands after.
          markAdmitted();
          if (
            event.type === 'thread-info' &&
            pendingStopSessionRef.current === myStreamSession
          ) {
            stopInFlightRef.current = stopTurnOnServer(
              event.threadId,
              event.runId,
            );
            break;
          }
          if (myStreamSession !== streamSessionIdRef.current) {
            break;
          }

          switch (event.type) {
            case 'text-delta':
              fullText += event.text;
              setStreamingText(fullText);
              break;

            case 'tool-call': {
              // Read now: the updater runs later, after more text may stream.
              const textBefore = fullText;
              localToolCalls.set(event.toolCallId, {
                toolName: event.toolName,
                args: event.args,
              });
              setActiveToolCalls((prev) => [
                ...prev,
                {
                  toolCallId: event.toolCallId,
                  toolName: event.toolName,
                  userFacingName: event.userFacingName,
                  isWrite: event.isWrite,
                  createdAt: Date.now(),
                  textBefore,
                  args: event.args,
                  status: 'pending',
                },
              ]);
              toolCallListenersRef.current.forEach((listener) => {
                try {
                  listener(
                    event.toolCallId,
                    event.toolName,
                    event.args,
                    event.isWrite,
                  );
                } catch (err) {
                  console.error('tool-call listener threw', err);
                }
              });

              // Awaitable frontend tools run their handler now (the backend is
              // blocking the agent until we POST a result) and round-trip the
              // return value back so the agent sees it in the same turn. Fire
              // async so the stream loop keeps receiving events.
              {
                const awaitableFt = frontendToolMap.get(event.toolName);
                if (
                  awaitableFt?.awaitable &&
                  !getClaimedToolCalls().has(event.toolCallId)
                ) {
                  markClaimed(
                    getClaimedToolCalls(),
                    awaitableFt,
                    event.toolCallId,
                  );
                  void runAwaitableTool(
                    awaitableFt,
                    event.toolCallId,
                    event.args,
                    frontendToolRunDeps,
                    getClaimedToolCalls(),
                    abortControllerRef.current?.signal,
                  );
                }
              }
              break;
            }

            case 'tool-result': {
              const tc = localToolCalls.get(event.toolCallId);
              completedToolResults.push({
                toolCallId: event.toolCallId,
                toolName: event.toolName,
                args: tc?.args ?? {},
                result: event.result,
              });
              setActiveToolCalls((prev) =>
                prev.map((existing) =>
                  existing.toolCallId === event.toolCallId
                    ? {
                        ...existing,
                        result: event.result,
                        status: 'complete' as const,
                      }
                    : existing,
                ),
              );
              // Execute fire-and-forget frontend tool handlers here. Awaitable
              // tools already ran on the tool-call event (and round-tripped
              // their result), so skip them to avoid double execution.
              //
              // The agent has already been handed the backend passthrough's
              // successful result by this point, so a failure here cannot reach
              // the current turn — it only marks the tool call failed in the UI
              // (and runs the tool's own onError). Make a tool awaitable if the
              // model needs to know whether it worked.
              const ftDef = frontendToolMap.get(event.toolName);
              if (ftDef && !ftDef.awaitable) {
                const { toolCallId } = event;
                void ftDef.execute(event.result as never).then((result) => {
                  if (!result.ok) {
                    console.error(
                      `frontend tool ${ftDef.name} failed`,
                      result.error,
                    );
                    markToolCallFailed(toolCallId, result);
                  }
                });
              }

              // Dispatch to module-registered listeners so module-specific
              // reactions (e.g. populating an AgentYield for the workflows
              // editor) stay out of this generic context.
              toolResultListenersRef.current.forEach((listener) => {
                try {
                  listener(event.toolCallId, event.toolName, event.result);
                } catch (err) {
                  console.error('tool-result listener threw', err);
                }
              });
              break;
            }

            case 'draft-snapshot':
              draftSnapshotListenersRef.current.forEach((listener) => {
                try {
                  listener(event.toolCallId, event.draft);
                } catch (err) {
                  console.error('draft-snapshot listener threw', err);
                }
              });
              break;

            case 'tool-input-delta':
              toolInputStreamListenersRef.current.forEach((listener) => {
                try {
                  listener(
                    event.toolCallId,
                    event.toolName,
                    event.partialInput,
                  );
                } catch (err) {
                  console.error('tool-input-delta listener threw', err);
                }
              });
              break;

            case 'submission':
              // The server found the thread busy (another tab, a turn this
              // tab didn't know of): the message waits in its queue, and
              // this tab follows the turn that will answer it.
              if (event.status === 'queued' && event.threadId) {
                const queuedThread = event.threadId;
                setMessages((prev) =>
                  prev.filter((m) => m.id !== userMessage.id),
                );
                setQueuedMessages((prev) => [
                  ...prev,
                  {
                    requestId: event.requestId,
                    text: userMessage.content,
                    mode: event.mode,
                  },
                ]);
                handedOff = true;
                void attachRef.current(queuedThread, moduleKey, 'refresh');
              }
              break;

            case 'thread-info':
              threadNamed?.();
              streamThreadId = event.threadId;
              // Now, not at the next render: Stop reads it with runIdRef.
              threadIdRef.current = event.threadId;
              setCurrentThreadId(event.threadId);
              runIdRef.current = event.runId ?? null;
              break;

            case 'assistant-message-saved':
              // Backend persisted the assistant turn after `finish`. Patch
              // the most recent assistant message with its DB id, traceId,
              // and observationId so user-feedback widgets have a durable
              // subject + Langfuse observation to attach to.
              setMessages((prev) => {
                for (let i = prev.length - 1; i >= 0; i--) {
                  if (prev[i]?.role === 'assistant') {
                    const next = prev.slice();
                    const target = next[i];
                    if (!target) return prev;
                    next[i] = {
                      ...target,
                      dbMessageId: event.messageId,
                      traceId: event.traceId,
                      observationId: event.observationId,
                    };
                    return next;
                  }
                }
                return prev;
              });
              break;

            case 'finish':
              closeReply();
              break;

            case 'submission-placed': {
              // A queued message joined the turn: what came before it is a
              // reply of its own, and the message follows it.
              closeReply();
              setActiveToolCalls([]);
              const { requestId } = event;
              forgetSubmissionScope(answeringRequestId);
              answeringRequestId = requestId;
              setActiveRequestId(requestId);
              setQueuedMessages((prev) =>
                prev.filter((q) => q.requestId !== requestId),
              );
              setMessages((prev) => [
                ...prev,
                {
                  id: generateMessageId(),
                  role: 'user',
                  content: event.text,
                  timestamp: new Date(),
                  attachments: event.attachments,
                },
              ]);
              break;
            }

            case 'error':
              setError(event.message);
              if (event.code === 'TOO_MANY_TURNS') {
                // Refused before anything was saved: the message wasn't sent.
                outcome = 'rejected';
                setMessages((prev) =>
                  prev.filter((m) => m.id !== userMessage.id),
                );
              }
              break;
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError(err instanceof Error ? err.message : 'Stream error');
          // The stream dropped, not the turn: follow it from its record.
          if (
            streamThreadId &&
            myStreamSession === streamSessionIdRef.current
          ) {
            handedOff = true;
            void attachRef.current(streamThreadId, moduleKey, 'drop');
          }
        }
      } finally {
        threadNamed?.();
        markAdmitted();
        if (!handedOff) forgetSubmissionScope(answeringRequestId);
        if (myStreamSession === streamSessionIdRef.current) {
          // The run ended: anything it didn't place was withdrawn with it.
          setQueuedMessages([]);
        }
        setIsLoading(false);
        // A newer send may already own the id; only clear this stream's own.
        setActiveRequestId((current) =>
          current === requestId ? null : current,
        );
        setIsCapturingScreen(false);
        setActiveToolCalls((prev) =>
          prev.some((tc) => tc.status === 'pending')
            ? prev.map((tc) =>
                tc.status === 'pending'
                  ? { ...tc, status: 'complete' as const }
                  : tc,
              )
            : prev,
        );
      }
      return outcome;
    },
    [
      dropHeldQueued,
      getClaimedToolCalls,
      frontendToolRunDeps,
      leaveConversation,
      markToolCallFailed,
      forgetSubmissionScope,
      queueMessage,
      recordSubmissionScope,
      stopRunningTurn,
    ],
  );

  const recordVoiceTurn = useCallback(
    (role: Message['role'], content: string, moduleKey: ModuleKey) => {
      restoreGenerationRef.current++;
      const prevModuleKey = moduleKeyRef.current;
      if (prevModuleKey !== null && !sharesThread(prevModuleKey, moduleKey)) {
        // Same rule as sendMessage: another module's thread is left behind,
        // its stream and yields with it.
        abortControllerRef.current?.abort();
        streamSessionIdRef.current++;
        stopRunningTurn();
        dropHeldQueued();
        leaveConversation();
        // A send still waiting (a Stop, a screenshot) is dropped without
        // its own cleanup: the old thread's loading state goes with it.
        setIsLoading(false);
        setActiveRequestId(null);
        threadIdRef.current = null;
        setCurrentThreadId(null);
        setMessages([]);
        setActiveYieldsState([]);
        setResolvedYields(new Map());
      }
      moduleKeyRef.current = moduleKey;
      setCurrentModuleKey(moduleKey);

      const message: Message = {
        id: generateMessageId(),
        role,
        content,
        timestamp: new Date(),
        channel: 'voice',
      };
      setMessages((prev) => [...prev, message]);
      const conversation = conversationRef.current;

      // Chained so a thread created by one save is reused by the next.
      voiceWriteRef.current = (voiceWriteRef.current ?? Promise.resolve())
        .then(async () => {
          // A request may be creating the thread right now: join it.
          await threadCreatingRef.current;
          const shown = () => conversationRef.current === conversation;
          const saved = await rpcClient.ai.chatAgent.recordVoiceTurn({
            threadId:
              (shown() ? threadIdRef.current : conversation.threadId) ??
              undefined,
            role,
            content,
          });
          conversation.threadId = saved.threadId;
          // Left meanwhile: the screen now shows another conversation.
          if (!shown()) return;
          if (threadIdRef.current !== saved.threadId) {
            threadIdRef.current = saved.threadId;
            setCurrentThreadId(saved.threadId);
          }
          if (role === 'assistant') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === message.id
                  ? { ...m, dbMessageId: saved.messageId }
                  : m,
              ),
            );
          }
        })
        .catch((err: unknown) => {
          // The turn stays on screen; only its saved copy is missing.
          console.warn('[agent-state] could not save a voice turn:', err);
        });
    },
    [dropHeldQueued, leaveConversation, stopRunningTurn],
  );

  const reset = useCallback(() => {
    restoreGenerationRef.current++;
    stopRunningTurn();
    leaveConversation();
    abortControllerRef.current?.abort();
    setMessages([]);
    setQueuedMessages([]);
    heldQueuedRef.current.clear();
    setStreamingText('');
    setIsLoading(false);
    setIsCapturingScreen(false);
    setError(null);
    setActiveToolCalls([]);
    setCurrentThreadId(null);
    setCurrentModuleKey(null);
    submissionScopesRef.current = {};
    pageKeyRef.current = undefined;
    setActiveYieldsState([]);
    setResolvedYields(new Map());
    streamSessionIdRef.current++;
    liveTurnRef.current = null;
  }, [leaveConversation, stopRunningTurn]);

  const restoreThread = useCallback(
    (thread: GetChatThreadOutput, moduleKey: ModuleKey) => {
      const restored = restoreMessages(thread.messages);

      setMessages(restored);
      setStreamingText('');
      setIsLoading(false);
      setIsCapturingScreen(false);
      setError(null);
      setActiveToolCalls([]);
      setCurrentThreadId(thread.id);
      setCurrentModuleKey(moduleKey);
    },
    [],
  );

  /** Frontend tools the page offers now; what a reattached turn runs against. */
  const liveFrontendToolsRef = useRef<
    ReadonlyMap<string, FrontendToolDefinition>
  >(new Map());
  const [pageModuleKey, setPageModuleKey] = useState<ModuleKey | null>(null);
  // Written where the page registers, never in render: an in-flight restore
  // reads it to see whether the page changed module meanwhile.
  const pageModuleKeyRef = useRef<ModuleKey | null>(null);

  /**
   * Acts on a reattached turn's snapshot as the `chat` stream would have: runs
   * the awaitable frontend tools still waiting on the browser. Finished calls are
   * not replayed to result listeners: the restored transcript already shows
   * them, and a listener may act on a result (publish, overlay) a second time.
   */
  const actOnLiveTurn = useCallback(
    (turn: LiveTurnState) => {
      // A page tool answers for the entity its page shows (reads too: another
      // workbook's handler would claim this one's call), so it resumes only
      // when the turn's recorded scope and the registered page's scope are
      // both known and equal. Nothing else resumes: a thread with no recorded
      // scope (a history observer) runs nothing, and navigation tools are not
      // retried on reattach (the agent's bridge times out and it can retry).
      const turnScope = turn.requestId
        ? submissionScopesRef.current[turn.requestId]
        : undefined;
      const onOriginPage =
        turnScope !== undefined && turnScope === liveScopeRef.current;
      for (const { call, tool } of claimPendingAwaitableCalls(
        turn.toolCalls,
        liveFrontendToolsRef.current,
        getClaimedToolCalls(),
        () => onOriginPage,
      )) {
        void runAwaitableTool(
          tool,
          call.toolCallId,
          call.args,
          frontendToolRunDeps,
          getClaimedToolCalls(),
          abortControllerRef.current?.signal,
        );
      }
    },
    [frontendToolRunDeps, getClaimedToolCalls],
  );

  const setLiveFrontendTools = useCallback(
    (
      tools: readonly FrontendToolDefinition[],
      moduleKey: ModuleKey | null,
      scope: string | null = null,
    ) => {
      liveFrontendToolsRef.current = toolsByName(tools);
      liveScopeRef.current = scope;
      pageModuleKeyRef.current = moduleKey;
      setPageModuleKey(moduleKey);
      // Workbook tools register after the page's data loads, which can be
      // after the reattached turn already asked for them.
      if (
        liveTurnRef.current &&
        liveTurnSessionRef.current === streamSessionIdRef.current &&
        !abortControllerRef.current?.signal.aborted
      ) {
        actOnLiveTurn(liveTurnRef.current);
      }
    },
    [actOnLiveTurn],
  );

  /**
   * Shows the thread's turn if one is still running server-side and keeps it
   * streaming until it ends. `refresh` loads the transcript only once a
   * running turn is found; `drop` (the stream died mid-turn) also settles a
   * turn that ended before the reattach, replacing the partial answer.
   */
  const attachToRunningTurn = useCallback(
    async (threadId: string, moduleKey: ModuleKey, mode: AttachMode) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const mySession = ++streamSessionIdRef.current;
      const current = () =>
        mySession === streamSessionIdRef.current && !controller.signal.aborted;
      liveTurnRef.current = null;
      const loadTranscript = async () => {
        const thread = await rpcClient.ai.chatAgent.getThread({ threadId });
        if (current()) restoreThread(thread, moduleKey);
      };
      // Promise chain, not try/finally: the React Compiler can't lower a
      // finalizer and would skip memoizing the whole provider.
      // The submission the live text answers; when it changes, a queued
      // message was placed and what came before it is in the transcript.
      let answering: string | undefined;
      await followLiveTurn(threadId, controller.signal, {
        onRunning: async (turn, first) => {
          const placedSince = !first && turn.requestId !== answering;
          if (placedSince) forgetSubmissionScope(answering);
          answering = turn.requestId;
          if (
            (first && (mode === 'refresh' || mode === 'placed')) ||
            placedSince
          ) {
            await loadTranscript();
          }
          if (!current()) return;
          runIdRef.current = turn.runId;
          setError(null);
          setIsLoading(true);
          setActiveRequestId(turn.requestId ?? null);
          setStreamingText(turn.text);
          setActiveToolCalls(liveToolCalls(turn));
          setQueuedMessages(queuedFromTurn(turn));
          // Per request: actOnLiveTurn runs tools only for a request this tab
          // submitted (it has a recorded scope); other turns stay observed.
          liveTurnRef.current = turn;
          liveTurnSessionRef.current = mySession;
          actOnLiveTurn(turn);
        },
        onSettled: async (turn) => {
          if (current()) liveTurnRef.current = null;
          forgetSubmissionScope(turn.requestId);
          // The finished turn is now an ordinary row in the transcript.
          await loadTranscript();
          if (current()) {
            setQueuedMessages([]);
            setError(
              turn.status === 'complete' ? null : (turn.error?.message ?? null),
            );
          }
        },
        settleUnseen: mode === 'drop' || mode === 'placed',
      })
        .catch((err: unknown) => {
          if (current()) {
            console.warn('Reattaching to the chat turn failed', err);
          }
        })
        .then(() => {
          if (current()) {
            liveTurnRef.current = null;
            setIsLoading(false);
          }
        });
    },
    [actOnLiveTurn, forgetSubmissionScope, restoreThread],
  );

  // sendMessage reattaches after a dropped stream; declared above this.
  useEffect(() => {
    attachRef.current = attachToRunningTurn;
  }, [attachToRunningTurn]);

  const loadThread = useCallback(
    (thread: GetChatThreadOutput, moduleKey: ModuleKey) => {
      restoreGenerationRef.current++;
      if (thread.id !== threadIdRef.current) {
        stopRunningTurn();
        leaveConversation();
        // Opened from history, not sent from a page: nothing here is this
        // tab's to answer, so a refresh keeps it an observer.
        submissionScopesRef.current = {};
      }
      abortControllerRef.current?.abort();
      streamSessionIdRef.current++;
      liveTurnRef.current = null;
      restoreThread(thread, moduleKey);
      setQueuedMessages([]);
      heldQueuedRef.current.clear();
      pageKeyRef.current = undefined;
      setActiveYieldsState([]);
      setResolvedYields(new Map());
      void attachToRunningTurn(thread.id, moduleKey, 'open');
    },
    [attachToRunningTurn, leaveConversation, restoreThread, stopRunningTurn],
  );

  useEffect(() => {
    if (currentThreadId && currentModuleKey) {
      rememberThread({
        threadId: currentThreadId,
        moduleKey: currentModuleKey,
        scopes: submissionScopesRef.current,
      });
    } else {
      rememberThread(null);
    }
  }, [currentThreadId, currentModuleKey]);

  useEffect(() => {
    if (!threadBeforeRefresh) return;
    void attachToRunningTurn(
      threadBeforeRefresh.threadId,
      threadBeforeRefresh.moduleKey,
      'refresh',
    );
  }, [attachToRunningTurn, threadBeforeRefresh]);

  // A refresh with no turn running still brings the transcript back, once the
  // page says which module it is: another module owns its own conversation.
  // A running turn restores it itself in `attachToRunningTurn`.
  const restoredAfterRefreshRef = useRef(false);
  useEffect(() => {
    if (!threadBeforeRefresh || pageModuleKey === null) return;
    if (restoredAfterRefreshRef.current) return;
    // Only the module that owns the thread uses up the restore: a fallback
    // module that registers first must not block it.
    if (!sharesThread(threadBeforeRefresh.moduleKey, pageModuleKey)) return;
    restoredAfterRefreshRef.current = true;
    if (threadIdRef.current !== null || isLoadingRef.current) return;
    const { threadId, moduleKey } = threadBeforeRefresh;
    const restoreSession = streamSessionIdRef.current;
    const restoreGeneration = restoreGenerationRef.current;
    rpcClient.ai.chatAgent
      .getThread({ threadId })
      .then((thread) => {
        // A turn or thread that took over meanwhile owns the screen, even one
        // that already finished before this response arrived.
        if (restoreSession !== streamSessionIdRef.current) return;
        if (restoreGeneration !== restoreGenerationRef.current) return;
        if (threadIdRef.current !== null || isLoadingRef.current) return;
        // The page may have moved to another module while this was in
        // flight: drop it, and let the owning module retry when it returns.
        const latestModuleKey = pageModuleKeyRef.current;
        if (
          latestModuleKey === null ||
          !sharesThread(moduleKey, latestModuleKey)
        ) {
          restoredAfterRefreshRef.current = false;
          return;
        }
        restoreThread(thread, moduleKey);
      })
      .catch((err: unknown) => {
        console.warn('Restoring the previous chat thread failed', err);
      });
  }, [pageModuleKey, restoreThread, threadBeforeRefresh]);

  const activeYieldsRef = useRef(activeYields);
  activeYieldsRef.current = activeYields;

  const resolveYield = useCallback(
    (toolCallId: string, decision: YieldDecision) => {
      const current = activeYieldsRef.current.find(
        (y) => y.toolCallId === toolCallId,
      );
      if (!current) return;
      setResolvedYields((prev) => {
        const next = new Map(prev);
        next.set(current.toolCallId, decision);
        return next;
      });
      setActiveYieldsState((prev) =>
        prev.filter((y) => y.toolCallId !== toolCallId),
      );
      const threadId = threadIdRef.current;
      // Fire-and-forget telemetry — surface errors to console so backend
      // outages don't block the UX.
      rpcClient.ai.chatAgent
        .recordYieldDecision({
          yieldId: current.yieldId,
          toolCallId: current.toolCallId,
          toolName: current.toolName,
          decision,
          threadId: threadId ?? undefined,
          pageKey: pageKeyRef.current,
        })
        .catch((err: unknown) => {
          console.warn('recordYieldDecision failed', err);
        });
    },
    [],
  );

  const clearYieldResolution = useCallback((toolCallId: string) => {
    setResolvedYields((prev) => {
      if (!prev.has(toolCallId)) return prev;
      const next = new Map(prev);
      next.delete(toolCallId);
      return next;
    });
  }, []);

  return (
    <AgentStateContext.Provider
      value={{
        messages,
        queuedMessages,
        withdrawQueued,
        canQueueFor: (moduleKey: ModuleKey) =>
          queueAllowed({
            isLoading,
            threadId: currentThreadId,
            currentModuleKey,
            moduleKey,
          }),
        streamingText,
        isLoading,
        activeRequestId,
        isCapturingScreen,
        error,
        activeToolCalls,
        currentThreadId,
        currentModuleKey,
        activeYields,
        setActiveYields,
        resolvedYields,
        sendMessage,
        recordVoiceTurn,
        reset,
        cancel,
        loadThread,
        resolveYield,
        clearYieldResolution,
        registerToolResultListener,
        registerToolCallListener,
        registerDraftSnapshotListener,
        registerToolInputStreamListener,
        setLiveFrontendTools,
      }}
    >
      {children}
    </AgentStateContext.Provider>
  );
}

export function useAgentState() {
  const context = useContext(AgentStateContext);
  if (context === undefined) {
    throw new Error('useAgentState must be used within an AgentStateProvider');
  }
  return context;
}
